import React, { useEffect, useMemo, useState } from "react";
import { dms, inr } from "./api";
import { PageHeader } from "./OwnerPages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { MessageCircle, Copy, RefreshCw } from "lucide-react";

// ============================================================================
// Salesperson: My Sales Report — today / week / month / custom, share on WhatsApp
// ============================================================================

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function presetRange(key) {
  const now = new Date();
  const t = ymd(now);
  if (key === "today") return [t, t];
  if (key === "yesterday") {
    const y = new Date(now); y.setDate(y.getDate() - 1);
    return [ymd(y), ymd(y)];
  }
  if (key === "week") {
    const s = new Date(now); s.setDate(s.getDate() - ((s.getDay() + 6) % 7)); // Monday
    return [ymd(s), t];
  }
  if (key === "month") return [ymd(new Date(now.getFullYear(), now.getMonth(), 1)), t];
  if (key === "last_month") {
    const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const e = new Date(now.getFullYear(), now.getMonth(), 0);
    return [ymd(s), ymd(e)];
  }
  return [t, t];
}

const PRESETS = [
  ["today", "Today"], ["yesterday", "Yesterday"], ["week", "This Week"],
  ["month", "This Month"], ["last_month", "Last Month"], ["custom", "Custom"],
];

const MODE_LABEL = { cash: "Cash", upi: "UPI", cheque: "Cheque", bank_transfer: "Bank Transfer", neft: "NEFT", rtgs: "RTGS", card: "Card" };

const fmtDate = (s) => {
  try {
    return new Date(s + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return s; }
};
const ltr = (n) => `${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 })} L`;
const rs = (n) => `₹${Math.round(Number(n || 0)).toLocaleString("en-IN")}`;

export function buildWhatsAppText(d, label) {
  if (!d) return "";
  const o = d.orders || {};
  const c = d.collections || {};
  const a = d.attendance || {};
  const period = d.date_from === d.date_to ? fmtDate(d.date_from) : `${fmtDate(d.date_from)} – ${fmtDate(d.date_to)}`;
  const L = [];
  L.push(`📊 *Sales Report – ${label}*`);
  L.push(`👤 ${d.salesperson?.name || ""}`);
  L.push(`📅 ${period}`);
  L.push("");
  L.push("🛒 *Orders*");
  L.push(`• Orders: ${o.orders || 0} (${o.retailers_ordered || 0} shops)`);
  L.push(`• Order value: ${rs(o.booked_value)}`);
  L.push(`• Litres: ${ltr(o.booked_liters)}`);
  if (o.billed_value) L.push(`• Billed: ${rs(o.billed_value)}`);
  if (o.pending_value) L.push(`• Pending: ${rs(o.pending_value)}`);
  L.push("");
  L.push("💰 *Collection*");
  L.push(`• Total: ${rs(c.total)} (${c.count || 0} payments)`);
  const modes = Object.entries(c.by_mode || {}).map(([k, v]) => `${MODE_LABEL[k] || k} ${rs(v)}`);
  if (modes.length) L.push(`• ${modes.join(" | ")}`);
  L.push("");
  L.push("🚶 *Field Work*");
  if (d.days > 1) L.push(`• Days worked: ${a.days_present || 0}`);
  L.push(`• Shop visits: ${a.visits || 0}`);
  if (a.km) L.push(`• Distance: ${a.km} km`);
  if (a.hours) L.push(`• Working hours: ${a.hours} h`);
  if (d.new_retailers) L.push(`• New retailers: ${d.new_retailers}`);
  if (d.target) {
    const t = d.target, p = d.pct || {};
    const row = (name, ach, tgt, f, fmt) => tgt ? `• ${name}: ${fmt(ach)} / ${fmt(tgt)}${p[f] != null ? ` (${p[f]}%)` : ""}` : null;
    const rows = [
      row("Value", d.achieved?.order_value, t.order_value, "order_value", rs),
      row("Litres", d.achieved?.order_liters, t.order_liters, "order_liters", ltr),
      row("Collection", d.achieved?.collection, t.collection, "collection", rs),
      row("Visits", d.achieved?.visits, t.visits, "visits", (n) => String(Math.round(n || 0))),
    ].filter(Boolean);
    if (rows.length) { L.push(""); L.push("🎯 *Target vs Achieved*"); L.push(...rows); }
  }
  if ((d.top_retailers || []).length) {
    L.push("");
    L.push("🏪 *Top Shops*");
    d.top_retailers.slice(0, 3).forEach((r, i) => L.push(`${i + 1}. ${r.name} – ${rs(r.value)}`));
  }
  if ((d.top_products || []).length) {
    L.push("");
    L.push("📦 *Top Products*");
    d.top_products.slice(0, 3).forEach((r, i) => L.push(`${i + 1}. ${r.name} – ${ltr(r.liters)}`));
  }
  return L.join("\n");
}

function Stat({ label, value, sub }) {
  return (
    <Card className="p-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-lg font-bold text-slate-900 mt-0.5">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </Card>
  );
}

function TargetBar({ name, ach, tgt, pct, fmt }) {
  if (!tgt) return null;
  const w = Math.min(100, pct || 0);
  const color = (pct || 0) >= 100 ? "bg-emerald-500" : (pct || 0) >= 60 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div>
      <div className="flex justify-between text-sm">
        <span className="text-slate-700">{name}</span>
        <span className="text-slate-600">{fmt(ach)} / {fmt(tgt)} <b>({pct ?? 0}%)</b></span>
      </div>
      <div className="h-2 rounded bg-slate-100 mt-1 overflow-hidden"><div className={`h-full ${color}`} style={{ width: `${w}%` }} /></div>
    </div>
  );
}

export default function SpSalesReportPage() {
  const [preset, setPreset] = useState("today");
  const [[from, to], setRange] = useState(presetRange("today"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const label = useMemo(() => {
    if (preset === "custom") return "Custom";
    return (PRESETS.find(p => p[0] === preset) || [])[1] || "";
  }, [preset]);

  const load = () => {
    if (!from || !to) return;
    setLoading(true);
    dms.spMySalesSummary({ date_from: from, date_to: to })
      .then(setData)
      .catch(e => toast.error(e?.response?.data?.detail || "Could not load the report"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (k) => {
    setPreset(k);
    if (k !== "custom") setRange(presetRange(k));
  };

  const text = useMemo(() => buildWhatsAppText(data, label), [data, label]);

  const shareWhatsApp = () => {
    if (!text) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };
  const copyText = async () => {
    try { await navigator.clipboard.writeText(text); toast.success("Report copied"); }
    catch { toast.error("Could not copy"); }
  };

  const o = data?.orders || {};
  const c = data?.collections || {};
  const a = data?.attendance || {};

  return (
    <div className="pb-24">
      <PageHeader title="My Sales Report" subtitle="Your sales summary — share it on WhatsApp" back="/dms" />

      <div className="flex flex-wrap gap-2 mb-3" data-testid="sp-report-presets">
        {PRESETS.map(([k, v]) => (
          <button key={k} onClick={() => pick(k)}
                  className={`px-3 py-1.5 rounded-full text-sm border ${preset === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}>
            {v}
          </button>
        ))}
      </div>

      {preset === "custom" && (
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div><Label className="text-xs">From</Label>
            <Input type="date" value={from} max={to} onChange={e => setRange([e.target.value, to])} className="mt-1" /></div>
          <div><Label className="text-xs">To</Label>
            <Input type="date" value={to} min={from} onChange={e => setRange([from, e.target.value])} className="mt-1" /></div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <Button onClick={shareWhatsApp} disabled={!data || loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sp-report-whatsapp">
          <MessageCircle size={16} className="mr-1.5" /> Send on WhatsApp
        </Button>
        <Button variant="outline" onClick={copyText} disabled={!data || loading}><Copy size={16} className="mr-1.5" /> Copy</Button>
        <Button variant="ghost" onClick={load} disabled={loading}><RefreshCw size={16} className={loading ? "animate-spin" : ""} /></Button>
      </div>

      {!data ? (
        <div className="text-sm text-slate-500">{loading ? "Loading…" : "No data"}</div>
      ) : (
        <div className="space-y-4">
          <div className="text-sm text-slate-600">
            {data.date_from === data.date_to ? fmtDate(data.date_from) : `${fmtDate(data.date_from)} – ${fmtDate(data.date_to)}`}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Order Value" value={inr(o.booked_value)} sub={`${o.orders || 0} orders · ${o.retailers_ordered || 0} shops`} />
            <Stat label="Litres" value={ltr(o.booked_liters)} sub={o.orders ? `Avg order ${rs(o.avg_order_value)}` : null} />
            <Stat label="Collection" value={inr(c.total)} sub={`${c.count || 0} payments`} />
            <Stat label="Shop Visits" value={a.visits || 0} sub={a.km ? `${a.km} km` : null} />
            <Stat label="Billed" value={inr(o.billed_value)} sub={`${o.dispatched_orders || 0} orders`} />
            <Stat label="Pending" value={inr(o.pending_value)} sub={`${o.pending_orders || 0} orders`} />
            <Stat label="Days Worked" value={a.days_present || 0} sub={a.hours ? `${a.hours} hours` : null} />
            <Stat label="New Retailers" value={data.new_retailers || 0} />
          </div>

          {data.target && (
            <Card className="p-4 space-y-3">
              <div className="font-semibold text-slate-800">Target vs Achieved</div>
              <TargetBar name="Order value" ach={data.achieved.order_value} tgt={data.target.order_value} pct={data.pct.order_value} fmt={rs} />
              <TargetBar name="Litres" ach={data.achieved.order_liters} tgt={data.target.order_liters} pct={data.pct.order_liters} fmt={ltr} />
              <TargetBar name="Collection" ach={data.achieved.collection} tgt={data.target.collection} pct={data.pct.collection} fmt={rs} />
              <TargetBar name="Visits" ach={data.achieved.visits} tgt={data.target.visits} pct={data.pct.visits} fmt={(n) => Math.round(n || 0)} />
            </Card>
          )}

          {Object.keys(c.by_mode || {}).length > 0 && (
            <Card className="p-4">
              <div className="font-semibold text-slate-800 mb-2">Collection by Mode</div>
              {Object.entries(c.by_mode).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm py-0.5"><span>{MODE_LABEL[k] || k}</span><span>{inr(v)}</span></div>
              ))}
            </Card>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-4">
              <div className="font-semibold text-slate-800 mb-2">Top Shops</div>
              {(data.top_retailers || []).length === 0 ? <div className="text-sm text-slate-500">No orders</div> :
                data.top_retailers.map((r, i) => (
                  <div key={i} className="flex justify-between text-sm py-0.5 gap-2">
                    <span className="truncate">{i + 1}. {r.name}</span><span className="shrink-0">{inr(r.value)}</span>
                  </div>
                ))}
            </Card>
            <Card className="p-4">
              <div className="font-semibold text-slate-800 mb-2">Top Products</div>
              {(data.top_products || []).length === 0 ? <div className="text-sm text-slate-500">No orders</div> :
                data.top_products.map((r, i) => (
                  <div key={i} className="flex justify-between text-sm py-0.5 gap-2">
                    <span className="truncate">{i + 1}. {r.name}</span><span className="shrink-0">{ltr(r.liters)}</span>
                  </div>
                ))}
            </Card>
          </div>

          {data.days > 1 && (data.by_day || []).length > 0 && (
            <Card className="p-4 overflow-x-auto">
              <div className="font-semibold text-slate-800 mb-2">Day by Day</div>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-500 text-xs">
                  <th className="py-1">Date</th><th className="text-right">Orders</th><th className="text-right">Value</th>
                  <th className="text-right">Litres</th><th className="text-right">Collection</th>
                </tr></thead>
                <tbody>
                  {data.by_day.map(r => (
                    <tr key={r.date} className="border-t border-slate-100">
                      <td className="py-1">{fmtDate(r.date)}{!r.present && <span className="ml-1 text-[10px] text-rose-500">no punch</span>}</td>
                      <td className="text-right">{r.orders}</td><td className="text-right">{rs(r.value)}</td>
                      <td className="text-right">{ltr(r.liters)}</td><td className="text-right">{rs(r.collection)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
